import { Pencil } from 'lucide-react';
import { DashboardChartSkeleton } from '@shigo-ui/dashboard-ui';
import { Button, Card, CardContent, SegmentedControl } from '@shigo-ui/ui';
import { ResponsiveLineChart } from '@shigo-ui/viz';

import { dashboardT } from '../../../../lib/messages';
import type { ReportRequest, ReportResponse } from '../model';
import { projectReportChart } from '../reportChartModel';
import { ReportTimeGranularity } from '../reportEnums';
import { TimeShiftControl } from './TimeShiftControl';

interface ReportChartPanelProps {
  request: ReportRequest;
  response?: ReportResponse | undefined;
  timeShiftResponse?: ReportResponse | undefined;
  loading: boolean;
  error: boolean;
  timeShiftDays: number | null;
  timeShiftLoading: boolean;
  timeShiftError: boolean;
  granularity: ReportTimeGranularity;
  disabled: boolean;
  hourDisabled: boolean;
  isGranularityDisabled?: (value: ReportTimeGranularity) => boolean;
  onGranularityChange: (value: ReportTimeGranularity) => void;
  onEditMetrics: () => void;
  onTimeShiftApply: (days: number) => void;
  onTimeShiftClear: () => void;
}

// About a quarter lower than the shared 300px plot; the table below carries the detail.
const reportChartHeight = 225;
// The shared skeleton/error height (340) leaves this much for the legend row.
const legendHeight = 40;
export function ReportChartPanel({
  request,
  response,
  timeShiftResponse,
  loading,
  error,
  timeShiftDays,
  timeShiftLoading,
  timeShiftError,
  granularity,
  disabled,
  hourDisabled,
  isGranularityDisabled,
  onGranularityChange,
  onEditMetrics,
  onTimeShiftApply,
  onTimeShiftClear,
}: ReportChartPanelProps) {
  const projection = projectReportChart(request, response, {
    response: timeShiftResponse,
    days: timeShiftDays,
  });

  return (
    <Card className="border-border bg-card min-w-0 rounded-md shadow-[var(--shadow-xs)]">
      {/* Phones: Time shift and Metrics share a row above a full-width granularity control. */}
      <div className="border-border flex flex-wrap items-center justify-between gap-2 border-b px-4 py-3 sm:justify-end sm:gap-3">
        <TimeShiftControl
          value={timeShiftDays}
          disabled={disabled}
          loading={timeShiftLoading}
          error={timeShiftError}
          onApply={onTimeShiftApply}
          onClear={onTimeShiftClear}
        />
        <SegmentedControl
          ariaLabel={dashboardT('reportExplorer.chart.granularity')}
          value={granularity}
          options={Object.values(ReportTimeGranularity).map((value) => ({
            value,
            label: dashboardT(`reportExplorer.chart.granularity.${value}`),
            disabled:
              (value === ReportTimeGranularity.Hour && hourDisabled) ||
              (isGranularityDisabled?.(value) ?? false),
          }))}
          disabled={disabled}
          onValueChange={onGranularityChange}
          className="order-last w-full sm:order-none sm:w-auto [&>button]:flex-1 sm:[&>button]:flex-none"
        />
        <Button type="button" variant="ghost" disabled={disabled} onClick={onEditMetrics}>
          <Pencil className="h-4 w-4" />
          {dashboardT('reportExplorer.metrics')}
        </Button>
      </div>
      <CardContent className="px-4 py-4">
        {loading ? (
          <DashboardChartSkeleton
            label={dashboardT('reportExplorer.chart.loading')}
            height={reportChartHeight + legendHeight}
          />
        ) : null}
        {!loading && error ? (
          <div
            className="text-destructive flex items-center justify-center text-sm"
            style={{ height: reportChartHeight + legendHeight }}
          >
            {dashboardT('reportExplorer.chart.error')}
          </div>
        ) : null}
        {!loading && !error ? (
          <ResponsiveLineChart
            ariaLabel={dashboardT('reportExplorer.chart.label')}
            axes={projection.axes}
            series={projection.series}
            emptyMessage={dashboardT('reportExplorer.chart.empty')}
            height={reportChartHeight}
          />
        ) : null}
      </CardContent>
    </Card>
  );
}
